import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../users/providers/UserProvider";
import ProfileAccountSheet from "./ProfileAccountSheet";
import ROUTES from "../../router/routesModel";
import { DISCOVERABLE_ROUTES } from "../navConfig";
import { Z } from "../../styles/zLayers";

/** Market page already has its own account control — avoid a duplicate avatar. */
const HIDE_ON = [ROUTES.POKEMON_CARDS];

/**
 * Routes that render their own market entry point and would otherwise show two.
 *
 * /map used to be in here: its chip row carried a third "Cards" chip, which was
 * never a map mode at all — it was a route change dressed as one. The collector
 * map's top row is now a two-up Map / Listings segmented control, and the
 * catalog is reached from this bar's grid button like it is everywhere else. One
 * control, one place, and the map's own row got shorter for it.
 */
const HIDE_MARKET_ON = [];

const BUTTON_PX = 40;
const GAP_PX = 8;
const INSET_PX = 12;

const matches = (routes, pathname) => routes.some((r) => pathname.startsWith(r));

/**
 * Horizontal band this floating bar occupies at the top-right, in px.
 *
 * The bar is fixed and sits above page content (z 1250), so any page that
 * renders its own header must keep content out of this zone rather than
 * stacking a third absolute layer in the same corner. Returns 0 where the bar
 * is hidden.
 *
 * DERIVES THE BUTTON COUNT FROM THE PATHNAME ALONE — it never sees the session,
 * so it still returns the two-button 112px band on a route where a signed-out
 * visitor now gets one button (#297); the one-button arithmetic below is 64px.
 * That is safe because every production caller sits on a RequireAuth route,
 * which never renders its child without a user (RequireAuth.jsx:17 returns null
 * before hydration, :19-27 redirects to /login after), so no signed-out render
 * ever reaches one:
 *   - CollectionPage.jsx:246     → ROUTES.COLLECTION, gated at Router.jsx:130
 *   - WishlistPage.jsx:102       → ROUTES.WISHLIST,   gated at Router.jsx:129
 *   - MapPage.jsx:1224/1672/2067 → ROUTES.MAP,        gated at Router.jsx:127
 * Add a caller on a public route and that stops holding: it would reserve the
 * 112px two-button band against a bar rendering one button. Take the session
 * into account here before you do — and note 11 test files depend on it (7
 * import it, 4 MapPage suites replace it with `() => 0`), so the signature is
 * load-bearing well beyond these three. Counting the stubs with a bare grep
 * gives 6 and is wrong: two of those matches are comments in
 * MapPage.mobileBranch and MapPage.revealExclusive stating that those suites
 * deliberately do NOT stub it, because stubbing it is what shipped P1-39.
 */
export const topChromeReservedPx = (pathname = "") => {
  if (matches(HIDE_ON, pathname)) return 0;
  const buttons = matches(HIDE_MARKET_ON, pathname) ? 1 : 2;
  return buttons * BUTTON_PX + (buttons - 1) * GAP_PX + INSET_PX * 2;
};

/**
 * Top-right account entry (profile is not in the bottom nav).
 * Floating so Map stays full-bleed.
 */
const TopAccountBar = () => {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (matches(HIDE_ON, location.pathname)) {
    return null;
  }

  // Signed out this button was a bounce: it navigates to
  // DISCOVERABLE_ROUTES.market (/pokemontcg), a RequireAuth route, so the tap
  // could only ever land on /login. The profile button below stays — signed out
  // it is labelled "Log in" and goes there deliberately, and it is the
  // visitor's only sign-in affordance in the top chrome.
  const showMarket = !!user && !matches(HIDE_MARKET_ON, location.pathname);
  const initial = Array.from(user?.name?.first || "")[0]?.toUpperCase() || "?";

  return (
    <>
      <div
        data-testid="top-account-bar"
        style={{
          position: "fixed",
          top: "max(12px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
          right: "max(12px, calc((100vw - 520px) / 2 + 12px))",
          // Chrome, not overlay: drawers and dialogs must cover this. At its old
          // 1250 it floated above modal scrims, staying lit and tappable while
          // a sheet was open.
          zIndex: Z.chrome,
          display: "flex",
          alignItems: "center",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {showMarket && (
        <button
          type="button"
          data-testid="top-market-link"
          aria-label="Open market"
          onClick={() => navigate(DISCOVERABLE_ROUTES.market)}
          style={{
            pointerEvents: "all",
            width: 40,
            height: 40,
            borderRadius: 12,
            border: "1px solid rgba(15,23,42,0.08)",
            background: "rgba(255,255,255,0.92)",
            boxShadow: "0 2px 10px rgba(15,23,42,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#475569",
          }}
        >
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
        )}

        <button
          type="button"
          data-testid="top-profile-button"
          aria-label={user ? "Account menu" : "Log in"}
          onClick={() => (user ? setSheetOpen(true) : navigate(ROUTES.LOGIN))}
          style={{
            pointerEvents: "all",
            width: 40,
            height: 40,
            borderRadius: 12,
            border: "2px solid rgba(109,40,217,0.25)",
            background: user ? "linear-gradient(135deg, #7c3aed, #4c1d95)" : "#e2e8f0",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            boxShadow: "0 2px 10px rgba(15,23,42,0.12)",
          }}
        >
          {/* The account photo. Decorative (`alt=""`) — the button carries the
              label — so it has no role to query by; the testid is how the
              P1-38 tests read which photo is actually on screen. */}
          {user?.image?.url ? (
            <img
              src={user.image.url}
              alt=""
              data-testid="top-profile-photo"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ color: user ? "#fff" : "#94a3b8", fontSize: 15, fontWeight: 700 }}>{initial}</span>
          )}
        </button>
      </div>

      <ProfileAccountSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};

export default TopAccountBar;
